import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import FastlyImage from '@components/common/fastly-image'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { CODE_ORG } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['aigc'].trialCourseQuery}`

export const CodeOrgSeo = formatSeoConfig({
  title: 'Code.org Courses for Kids: Live Online Classes',
  description:
    'Code.org classes for kids with expert instructors. Interactive projects and a structured K–12 curriculum at Codingal. Try a free lesson today.',
  url: 'https://www.codingal.com/code-org-courses-for-kids/',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v1.jpg',
})

export const BegineerGuide = () => (
  <div className="space-y-3">
    <p>
      In our Code.org for Kids module, part of the Codingal Grandmaster Course
      for grades 1-3, children dive into engaging and hands-on projects that
      make learning coding both fun and educational. They start with basic
      coding concepts and progress through interactive lessons where they get to
      create their own games, animations, and projects. Here are some exciting
      activities they will explore:
    </p>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/code-org-1.jpg`}
        alt="Code.org projects for kids in grades 1-3"
        width={512}
        height={341}
      />
    </div>

    <ul>
      <li>
        Let’s Learn Sign Language: Build an interactive project to help children
        learn and practice sign language using simple coding techniques.
      </li>
      <li>
        My Slam Book: Create a personalized digital slam book where kids can
        design their own pages, input information, and showcase their creations.
      </li>
      <li>
        Super Kiddo: Develop a superhero-themed game that encourages logical
        thinking and problem-solving as kids apply coding skills to create
        challenges and interactions.
      </li>
      <li>
        Crypto Mastery: Learn about cryptography by building a fun project that
        introduces kids to encryption in a simple and hands-on way.
      </li>
      <li>
        Binary Brilliance: Dive into the world of binary numbers through fun
        projects that teach kids how computers understand and process data.
      </li>
      <li>
        Treasure Hunt: Design a thrilling game where kids apply their coding
        skills to create an interactive treasure hunt that’s both fun and
        educational.
      </li>
    </ul>
    <p>
      These projects not only help kids learn basic coding but also inspire them
      to think critically, solve problems, and unleash their creativity while
      building interactive and fun projects.
    </p>
    <p>
      Code.org is an excellent platform for introducing children to the world of
      coding! By teaching kids how to program through fun, interactive projects,
      they’ll learn essential skills like problem-solving, logic, and
      computational thinking. It’s like giving kids the tools to create their
      own digital world and solve real-world problems. Here’s how to get started
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      1. What is Code.org for Kids?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/learn-code.jpg`}
        alt="Child learning basic coding concepts with Code.org’s engaging activities"
        width={512}
        height={341}
      />
    </div>
    <p>Learning HTML is a great way for kids to:</p>
    <ul>
      <li>
        Code.org makes coding accessible by teaching kids how to build
        interactive games, animations, and websites.
      </li>
      <li>
        It uses block-based programming to break down coding concepts into
        easy-to-understand steps, allowing children to quickly start building
        their own digital creations.
      </li>
      <li>
        Real-world examples like Minecraft and Flappy Bird help kids see the
        magic of coding firsthand!
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">2. Fun Coding Activities</h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/code-activities.jpg`}
        alt="Kids playing coding games and learning with Code.org projects"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Children get to create games, design animations, and even build apps
        through Code.org’s interactive coding projects.
      </li>
      <li>
        Projects like Minecraft and Flappy Bird offer kids a fun, game-based way
        to explore coding.
      </li>
      <li>
        These hands-on activities ensure that coding is both fun and
        educational, helping kids stay engaged.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      3. Encourage Critical Thinking
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/critical-thinking.jpg`}
        alt="Child learning to code on Code.org on his computer and build games like Flappy Bird and Minecraft for kids"
        width={512}
        height={341}
      />
    </div>
    <p>
      By working with Code.org, children develop problem-solving skills,
      learning how to break down tasks and find solutions through logic and
      creativity.They begin to think critically about how their creations work,
      why certain elements function the way they do, and how to improve them.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      4. Making Coding Accessible and Fun
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/code-accessible.jpg`}
        alt="Child building an interactive game using Code.org tools"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        The beauty of Code.org is that kids can start learning coding from as
        young as 5 years old.
      </li>
      <li>
        With beginner-friendly platforms and block-based coding, kids of all
        ages can dive into coding in a simple and fun way.
      </li>
      <li>
        The platform’s approach ensures kids grasp fundamental concepts like
        sequencing, loops, and events, with plenty of creative challenges along
        the way.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      5. Fun Benefits of Learning Coding for Kids
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/benefits.jpg`}
        alt="Benefits of learning coding for kids"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>Turns problem-solving into a fun challenge</li>
      <li>Helps kids break big ideas into simple steps</li>
      <li>Sparks creativity while finding smart solutions</li>
      <li>Express themselves by building their own web projects</li>
      <li>Makes them curious about how their games and apps work</li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      6. Learn Through Games and Fun Challenges
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/learn-games.jpg`}
        alt="Kids learning coding by creating and playing interactive games like Minecraft and Flappy Bird on Code.org"
        width={512}
        height={341}
      />
    </div>
    <p>
      Code.org uses popular games like Minecraft and Flappy Bird to make
      learning feel like play. Kids explore key coding concepts in a fun,
      hands-on way. The interactive platform lets them see their creations come
      to life instantly.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      7. Safe and Secure Learning Environment
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/secure-env.jpg`}
        alt="Child learning to code safely in an online environment"
        width={512}
        height={341}
      />
    </div>
    <p>
      Code.org ensures that children can learn to code in a safe online space.
      It uses age-appropriate materials and follows privacy guidelines, so kids
      can learn without worry. Parents can rest assured that their child is
      learning coding in a secure, educational setting.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      8. Start Learning Today with Code.org
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/code-org-2.jpg`}
        alt="Child on a laptop starting their Code.org learning journey"
        width={512}
        height={341}
      />
    </div>
    <p>
      Start small by trying an interactive tutorial or simple coding challenge
      with Code.org’s free platform. Encourage your child to experiment,
      explore, and learn from their mistakes in a supportive environment where
      they can grow their curiosity and creativity.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      9. Take the First Step Together
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/code-org/code-org-3.jpg`}
        alt="free  Code.org classes for kids"
        width={512}
        height={341}
      />
    </div>
    <p>
      Begin your child’s coding adventure with Code.org and help them explore
      the world of programming with fun, hands-on projects. Get started with a
      free trial lesson today at Codingal, where experienced tutors will guide
      your child through exciting and interactive coding lessons.
    </p>
  </div>
)

const CodeOrg = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...CodeOrgSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[CODE_ORG].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn Code.org with Top AI & Coding Course Instructors for Kids and Teens"
        description={`At Codingal, we make coding a thrilling adventure for kids! Code.org's incredible platform comes to life in our curriculum, where children embark on a fun-filled journey through interactive projects. Kids will dive into creating animations, building games, and designing apps, all while learning essential coding concepts. Through projects like "Animated Alien," "Space Shooter," and "Dance Party," children will see characters move, dance, and come to life with the power of code! They'll also have the chance to develop real-world applications, like calculators and health guides, while collaborating with AI to create Digital Privacy Advisors and Math Table Assistants that make learning fun and interactive. By the end of the course, your child will be amazed by their newfound coding skills, realizing that with Code.org, they can build anything they imagine!`}
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={CODE_ORG}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={CODE_ORG} />
          <CourseFeatures
            courseTitle={CODE_ORG}
            title={<Trans>Engaging Website Development classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={CODE_ORG}
            title="Our Code.org for Kids Class Curriculum"
            description={
              <Trans>
                At Codingal, students follow a seamless progression in learning
                coding through our curriculum that integrates Code.org&lsquo;s
                engaging platform, with each stage tailored to their age and
                skill level. In Grades 1–3, they explore foundational coding
                concepts through exciting projects like
                &ldquo;Algorithms,&rdquo; &ldquo;Real World Simulation,&rdquo;
                and &ldquo;Custom Behaviors&rdquo; while mastering block-based
                programming fundamentals including sequences, events, and basic
                problem-solving.In Grades 4–5, they advance with thrilling
                projects like &ldquo;The Astro Rescue Game,&rdquo; &ldquo;Train
                your Turtle,&rdquo; and &ldquo;Collision Detection,&rdquo;
                building on their foundation with animation basics and
                JavaScript introduction. Students progress from simple
                drag-and-drop coding to creating interactive games and apps,
                developing essential computational thinking skills through
                hands-on projects.At every step, students build solid coding
                skills, problem-solving abilities skills, problem-solving
                abilities, and confidence to succeed in our tech-driven world
                through our curriculum that seamlessly integrates
                Code.org&lsquo;s proven platform.
              </Trans>
            }
          />
          <Activities courseName={CODE_ORG} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning
                  Code.org a truly supported experience, especially through our
                  virtual Code.org lessons for kids that connect students with
                  top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Code.org
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create the perfect environment for kids to
                  explore the world of artificial intelligence using
                  beginner-friendly Code.org lessons and an introduction to the
                  world of block-based programming. Our curriculum is designed
                  to make learning fun and effective, combining hands-on
                  projects, creative coding challenges, and real-world tools
                  that help young learners master the fundamentals of AI.
                </span>
              </Trans>
            }
            courseName={CODE_ORG}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters Code.org through a STEM.org
                  accredited program built on proven educational frameworks like
                  Bloom&lsquo;s Taxonomy, BIDE, and STEAM. Our curriculum is
                  aligned with Common Core, state standards, AP Computer Science
                  goals, and the K–12 Computer Science Framework, ensuring
                  learning that&lsquo;s both age-appropriate and college-ready.
                  Through hands-on, project-based learning, students create
                  real-world Code.org applications like interactive games and
                  AI-powered apps, applying math, science, and engineering logic
                  to solve everyday problems. Backed by STEM.org&lsquo;s
                  rigorous audits, every Code.org lesson lays a strong,
                  future-proof foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Code.org certifications for kids"
            description={
              <>
                Students progress through a series of Code.org lessons, starting
                with logic building and advancing to more complex projects. Upon
                successful completion of the Code.org curriculum, students will
                earn the STEM.org certified Code.org Certificate. This
                certification is a valuable addition to academic portfolios,
                school projects, and future tech applications. With hands-on
                experience and a strong portfolio of 20+ projects, learners will
                gain confidence and be ready for future studies and careers in
                coding and artificial intelligence.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Code.org Classes Are Perfect for Elementary and Middle School Students "
            description={
              <>
                <p>
                  At Codingal, we make learning to code fun and easy for
                  elementary and middle school students. Our Code.org classes
                  introduce kids to the basics of coding through interactive,
                  block-based activities that are designed to spark creativity.
                  Students start with fundamental concepts such as algorithms,
                  sequencing, logic building, and events. They begin by creating
                  simple animations and games, gradually moving on to more
                  complex projects like interactive stories and puzzles.
                </p>
                <p>
                  As kids progress, they build problem-solving skills and
                  develop critical thinking, making coding both enjoyable and
                  easy to understand. By the end of the course, students will
                  have a strong foundation in programming, setting them up for
                  future success in coding and technology.
                </p>
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['cgm', 'sp', 'aigc']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>Code.org for Kids: A Beginner’s Guide for Parents</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={CODE_ORG}
            title="Frequently Asked Questions (FAQs) about Code.org"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={CODE_ORG}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Code.org for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default CodeOrg
